import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Fuse from 'fuse.js';
import { FormattedMessage, useIntl } from 'react-intl';
import Input from './core/Input';
import { cn } from '@/helpers/classnames';
import useClickOutside from '@/hooks/useClickOutside';

// Organisations can have thousands of travellers. Fuzzy-searching that list
// and mounting every match blocks the main thread while typing, so large
// lists use a substring match and only the first results are rendered.
const RESULT_LIMIT = 100;

interface Option {
  label: string;
  value: string;
  description?: string;
  endContent?: React.ReactNode;
}

interface AutocompleteProps extends React.HTMLAttributes<HTMLInputElement> {
  options: Option[];
  value: string;
  label?: string;
  helperText?: string;
  helperTextReserveSpace?: boolean;
  error?: string;
  onValueChange: (value: string) => void;
  onSearchChange?: (search: string) => void;
  placeholder: string;
  id: string;
  autoFocus?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  className?: string;
}

const Autocomplete = forwardRef<HTMLInputElement, AutocompleteProps>(
  (
    {
      options,
      value,
      label,
      helperText,
      helperTextReserveSpace,
      error,
      onValueChange,
      onSearchChange,
      placeholder,
      id,
      autoFocus,
      startAdornment,
      endAdornment,
      className,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const { formatMessage } = useIntl();

    const [search, setSearch] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [selectedOption, setSelectedOption] = useState<Option | undefined>(
      undefined,
    );

    const [isOpen, setIsOpen] = useState(false);
    const [popoverTop, setPopoverTop] = useState<number>();

    const fuse = useMemo(() => {
      if (options.length > RESULT_LIMIT) {
        return null;
      }

      return new Fuse(options, {
        keys: ['label'],
        threshold: 0.1,
        distance: 1000,
      });
    }, [options]);

    const indexedLabels = useMemo(() => {
      if (options.length <= RESULT_LIMIT) {
        return [];
      }
      return options.map((option) => option.label.toLowerCase());
    }, [options]);

    const { matchingOptions, matchCount } = useMemo(() => {
      if (fuse) {
        const matches = search
          ? fuse.search(search).map((result) => result.item)
          : options;
        return { matchingOptions: matches, matchCount: matches.length };
      }

      const needle = search.trim().toLowerCase();
      if (!needle) {
        let visibleOptions = options.slice(0, RESULT_LIMIT);
        if (value && !visibleOptions.some((option) => option.value === value)) {
          const selected = options.find((option) => option.value === value);
          if (selected) {
            visibleOptions = [
              selected,
              ...visibleOptions.slice(0, RESULT_LIMIT - 1),
            ];
          }
        }
        return { matchingOptions: visibleOptions, matchCount: options.length };
      }

      const visibleOptions: Option[] = [];
      let count = 0;
      for (let index = 0; index < options.length; index++) {
        if (!indexedLabels[index]?.includes(needle)) {
          continue;
        }
        count += 1;
        if (visibleOptions.length < RESULT_LIMIT) {
          visibleOptions.push(options[index]);
        }
      }

      return { matchingOptions: visibleOptions, matchCount: count };
    }, [fuse, indexedLabels, options, search, value]);

    const scrollToElement = useCallback((element: HTMLElement) => {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }, []);

    const onBlur = useCallback(() => {
      setIsOpen(false);
      setSearch(options.find((o) => o.value === value)?.label || '');
    }, [options, value]);

    const onFocus = useCallback((autoSelect: boolean = false) => {
      setIsOpen(true);
      setPopoverTop(inputRef.current?.offsetHeight);
      setFocusedIndex(-1);
      if (autoSelect) {
        inputRef.current?.setSelectionRange(0, 999);
      }
    }, []);

    const onOptionChange = useCallback(
      (option: Option) => {
        setSelectedOption(option);
        onValueChange(option.value);
        onBlur();
      },
      [onValueChange, onBlur],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!matchingOptions || document.activeElement !== inputRef.current)
          return;

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setFocusedIndex((prev) => {
              const next = prev + 1;
              if (next >= matchingOptions.length) return prev;
              const element = document.getElementById(
                matchingOptions[next].value,
              );
              if (element) {
                scrollToElement(element);
              }
              return next;
            });
            break;
          case 'ArrowUp':
            e.preventDefault();
            setFocusedIndex((prev) => {
              const next = prev - 1;
              if (next < 0) return prev;
              const element = document.getElementById(
                matchingOptions[next].value,
              );
              if (element) {
                scrollToElement(element);
              }
              return next;
            });
            break;
          case 'Enter':
            e.preventDefault();
            if (isOpen) {
              onBlur();
            } else {
              onFocus();
            }
            if (focusedIndex >= 0 && focusedIndex < matchingOptions.length) {
              const option = matchingOptions[focusedIndex];
              onOptionChange(option);
            }
            break;
          case 'Tab':
            onBlur();
            break;
          case 'Escape':
            e.preventDefault();
            onBlur();
            break;
        }
      },
      [
        matchingOptions,
        focusedIndex,
        isOpen,
        scrollToElement,
        onOptionChange,
        onBlur,
        onFocus,
      ],
    );

    useClickOutside([inputRef, popoverRef], !isOpen, () => {
      onBlur();
    });

    // Options change while the person types, as search results arrive, so the
    // text follows the value only when the value itself changes.
    const syncedValueRef = useRef('');
    useEffect(() => {
      if (value === syncedValueRef.current) {
        return;
      }

      if (!value) {
        syncedValueRef.current = '';
        setSelectedOption(undefined);
        return;
      }

      const selected = options.find((option) => option.value === value);
      if (!selected) {
        return;
      }

      syncedValueRef.current = value;
      setSelectedOption(selected);
      setSearch(selected.label);
    }, [options, value]);

    const listboxId = `${id}-listbox`;
    const activedescendantId =
      focusedIndex >= 0 && matchingOptions[focusedIndex]
        ? matchingOptions[focusedIndex].value
        : undefined;

    const resultsAnnouncement = useMemo(() => {
      if (!isOpen || !search) return '';
      if (matchCount > matchingOptions.length) {
        return formatMessage(
          {
            defaultMessage:
              '{count} resultat hittades. Visar de första {shown}.',
          },
          { count: matchCount, shown: matchingOptions.length },
        );
      }
      return formatMessage(
        {
          defaultMessage:
            '{count, plural, =0 {Inga resultat hittade} one {Ett resultat hittades} other {{count} resultat hittades}}',
        },
        { count: matchCount },
      );
    }, [isOpen, search, matchCount, matchingOptions.length, formatMessage]);

    return (
      <div className="relative" ref={ref}>
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {resultsAnnouncement}
        </div>
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            onSearchChange?.(e.target.value);
            if (!isOpen) {
              onFocus();
            }
          }}
          id={id}
          onFocus={() => onFocus(true)}
          autoFocus={autoFocus}
          onKeyDown={handleKeyDown}
          startAdornment={startAdornment}
          endAdornment={endAdornment}
          label={label || ''}
          helperText={helperText}
          helperTextReserveSpace={helperTextReserveSpace}
          error={error}
          className={className}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={isOpen}
          aria-activedescendant={activedescendantId}
        />
        {isOpen && (
          <div
            className="absolute z-50 flex max-h-64 w-full overflow-hidden rounded-md border border-background-300 bg-background-50"
            style={{ top: popoverTop }}
            ref={popoverRef}
          >
            {/* Options are plain elements, not Button. A listbox may only
                contain options, and an option's parent must be the listbox.
                Button stays a button, and asChild still wraps content in a
                fragment and sets type="button". */}
            <div
              id={listboxId}
              className="flex w-full flex-col gap-1 overflow-y-auto p-2"
              role="listbox"
            >
              {matchingOptions.map((option, index) => {
                const isSelected = selectedOption?.value === option.value;
                const isFocused = focusedIndex === index;
                return (
                  <div
                    key={option.value}
                    id={option.value}
                    role="option"
                    aria-selected={isSelected}
                    aria-setsize={matchCount}
                    aria-posinset={index + 1}
                    className={cn(
                      'flex w-full min-w-0 cursor-pointer flex-col items-start rounded-md px-3 py-2 text-left font-normal',
                      isFocused && 'ring-2 ring-inset ring-primary-500',
                      isSelected
                        ? 'bg-primary-500 text-contrast-primary'
                        : 'bg-background-50 hover:bg-background-200',
                    )}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onOptionChange(option);
                      setFocusedIndex(index);
                    }}
                  >
                    <span className="w-full truncate text-left">
                      {option.label}
                    </span>
                    {option.description && (
                      <span className="opacity-75">{option.description}</span>
                    )}
                    {option.endContent}
                  </div>
                );
              })}
              {matchingOptions.length === 0 && (
                <div
                  role="option"
                  aria-disabled="true"
                  aria-selected={false}
                  className="w-full min-w-0 justify-start px-3 py-2 font-normal italic opacity-75"
                >
                  <FormattedMessage defaultMessage="Inga resultat hittade" />
                </div>
              )}
              {matchCount > matchingOptions.length && (
                <div
                  role="option"
                  aria-disabled="true"
                  aria-selected={false}
                  className="w-full min-w-0 justify-start px-3 py-2 font-normal italic opacity-75"
                >
                  <FormattedMessage
                    defaultMessage="Visar de första {shown} av {total}."
                    values={{
                      shown: matchingOptions.length,
                      total: matchCount,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export { Autocomplete };
